import React from 'react';

import * as localStorage from '../libs/local-storage';
import { LAYOUT_PREFERENCES } from '../types/preferences';

const LAYOUT_PREFERENCES_PATH = 'layout_preferences';

/**
 * Gets a layout preference value from localStorage
 *
 * @template K - Key of LAYOUT_PREFERENCES
 * @param key - The layout preference key to read
 * @returns The stored value or undefined if not found
 */
export const getLayoutPref = <K extends keyof LAYOUT_PREFERENCES>(key: K): LAYOUT_PREFERENCES[K] | undefined => {
  const stored = localStorage.get({ path: `${LAYOUT_PREFERENCES_PATH}.${key}` });
  return stored !== false ? (stored as LAYOUT_PREFERENCES[K]) : undefined;
};

/**
 * Sets a layout preference value in localStorage
 *
 * @template K - Key of LAYOUT_PREFERENCES
 * @param key - The layout preference key to write
 * @param value - The value to store
 */
export const setLayoutPref = <K extends keyof LAYOUT_PREFERENCES>(key: K, value: LAYOUT_PREFERENCES[K]): void => {
  localStorage.set({ data: value, path: `${LAYOUT_PREFERENCES_PATH}.${key}` });
};

/**
 * Hook for reading and writing layout preferences to localStorage
 *
 * @template K - Key of LAYOUT_PREFERENCES
 * @param key - The layout preference key to read/write
 * @param defaultValue - The default value if not found in storage
 * @returns A tuple of [value, setValue] similar to useState
 *
 * @example
 * const [sidebarSize, setSidebarSize] = useLayoutPreference('definition_sidebar_size', '400px');
 */
const useLayoutPreference = <K extends keyof LAYOUT_PREFERENCES>(
  key: K,
  defaultValue: LAYOUT_PREFERENCES[K]
): [LAYOUT_PREFERENCES[K], (_value: LAYOUT_PREFERENCES[K]) => void] => {
  const [value, setValue] = React.useState<LAYOUT_PREFERENCES[K]>(() => getLayoutPref(key) ?? defaultValue);

  const setAndPersist = React.useCallback(
    (newValue: LAYOUT_PREFERENCES[K]) => {
      setValue(newValue);
      setLayoutPref(key, newValue);
    },
    [key]
  );

  return [value, setAndPersist];
};

export default useLayoutPreference;
